import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';

import usePageTitle from '../../hooks/UsePageTitle';

const ERPCustomization = () => {
  usePageTitle('Customising & Maintenance of ERP');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Customising & Maintenance of ERP</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/erp1.webp' 
            alt="ERP Customization" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              Enterprise Resource Planning systems are the digital backbone of modern organizations. 
              But too often, businesses either underutilize their ERP investment or struggle with 
              rigid, one-size-fits-all solutions that don't match their actual workflows.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, we bridge the gap between business operations and technology by 
              providing expert ERP Customization & Maintenance Services that are practical, scalable, 
              and tailored to your unique needs.
            </p>
            <p className="text-lg line-height-3 mt-3">
              Whether you are implementing a new ERP, fine-tuning your existing system, or integrating 
              new modules, our team ensures that your ERP becomes a true enabler of efficiency, 
              compliance, and decision-making.
            </p>
          </Card>
        </div>

        {/* Why ERP Customization Matters Section */}
        <div className="col-12 mt-5">
          <h2>Why ERP Customization Matters</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <img 
              src='/images/erp2.jpg' 
              alt="ERP Customization Importance" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '300px', objectFit: 'cover' }}
            />
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Every business has its own rhythm — from the way it manages inventory and books sales 
                to how it approves expenses or handles payroll. A generic ERP might force you to change 
                your processes to fit its structure, leading to frustration, inefficiencies, and poor adoption.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold text-primary">
                We customize the ERP to fit your business, not the other way around.
              </p>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="col-12 mt-5">
          <h2>Our Core ERP Services</h2>
          <Divider />
          
          <div className="grid">
            {/* Service 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Business Process Mapping" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>End-to-end mapping of your operational, financial, and compliance processes.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identifying key process bottlenecks, duplication, or manual dependencies.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Defining automation opportunities within workflows (sales, procurement, HR, etc.).</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. ERP Needs Assessment" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of your current ERP or accounting system.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identifying gaps between available features and real-world business needs.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Selection or recommendation of ERP tools aligned with your size, industry, and growth plans.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. Customization & Module Development" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Custom design of dashboards, reports, data entry interfaces, and approval flows.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Development of custom modules.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Workflow automation including alerts, escalations, and system-based validations.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Integration Services" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Seamless integration of ERP with third-party tools.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>API-based synchronization for real-time data exchange between systems.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Cross-system reconciliation.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Data Migration & Clean-up" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Structured migration from legacy systems or spreadsheets into ERP.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Data sanity checks, duplicate removal, and normalization for optimal reporting.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Historical data import for continuity in financial and compliance records.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. User Training & Change Management" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>End-user training across departments to improve adoption.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>SOP documentation and video tutorials for reference.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Helpdesk setup and handholding during initial roll-out.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Ongoing Maintenance & Support" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Bug-fixing, module enhancement, and interface updates.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Monthly health-checks of data quality.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Compliance & Audit-Ready Setup" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Ensuring your ERP captures and stores all compliance-critical data.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Configuration of audit trails, access logs, and role-based data security.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Designing real-time dashboards and alerts for statutory due dates, tax liabilities, and regulatory compliance.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 9 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="9. Advanced Analytics & Reporting" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Design of management dashboards and real-time KPIs.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Custom reports for profitability analysis, cost centers, product-wise margins, and department efficiency.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Predictive insights based on historical data to support strategic decisions.</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <h2>Power Your Operations with a Tailored ERP System</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                At SnP Finvisors, we ensure your ERP is not just a piece of software — it becomes a true 
                partner in your business success. With smart customization, disciplined implementation, 
                and ongoing support, we turn your ERP into a competitive advantage.
              </p>
            </div>
            <img 
              src='/images/erp3.jpg' 
              alt="ERP Solutions" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '300px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERPCustomization;